import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { csrfToken } from "../utils/csrf"

// Adding files to a project: drop them on the page or in the dialog, or choose several.
// Every file is its own request (JSON, XHR for real progress), three at a time, so a
// rejected file - wrong type, too big, over quota - never blocks the rest. A batch ends
// with a summary; when everything went through the page reloads to show the new files.
export default class extends Controller {
    static targets = ["dialog", "zone", "input", "list", "summary", "actions", "retry", "overlay"]
    static values = { url: String, maxBytes: Number, types: String }

    static CONCURRENCY = 3
    static MAX_FILES = 50

    connect() {
        this._depth = 0
        this._queue = []
        this._active = 0
        this._rows = []
        if (new URLSearchParams(window.location.search).has("files")) this.openDialog()
    }

    // --- Choosing -------------------------------------------------------------

    chosen() {
        this.add(this.inputTarget.files)
        this.inputTarget.value = ""
    }

    zoneOver(event) {
        event.preventDefault()
        event.stopPropagation()
        this.zoneTarget.classList.add("is-over")
    }

    zoneLeave(event) {
        event.preventDefault()
        this.zoneTarget.classList.remove("is-over")
    }

    zoneDrop(event) {
        event.preventDefault()
        event.stopPropagation()
        this._depth = 0
        this.zoneTarget.classList.remove("is-over")
        this.hideOverlay()
        this.add(event.dataTransfer.files)
    }

    // --- Dropping anywhere on the page ---------------------------------------------

    pageEnter(event) {
        if (!this.hasFiles(event)) return
        event.preventDefault()
        this._depth += 1
        if (this.hasOverlayTarget) this.overlayTarget.hidden = false
    }

    pageOver(event) {
        if (this.hasFiles(event)) event.preventDefault()
    }

    pageLeave(event) {
        if (!this.hasFiles(event)) return
        this._depth = Math.max(0, this._depth - 1)
        if (this._depth === 0) this.hideOverlay()
    }

    pageDrop(event) {
        if (!this.hasFiles(event)) return
        event.preventDefault()
        this._depth = 0
        this.hideOverlay()
        this.openDialog()
        this.add(event.dataTransfer.files)
    }

    hasFiles(event) {
        return event.dataTransfer && Array.from(event.dataTransfer.types || []).includes("Files")
    }

    hideOverlay() {
        if (this.hasOverlayTarget) this.overlayTarget.hidden = true
    }

    openDialog() {
        if (!this.hasDialogTarget) return
        Modal.getOrCreateInstance(this.dialogTarget).show()
    }

    // --- The batch ------------------------------------------------------------------

    add(fileList) {
        const files = Array.from(fileList || [])
        if (!files.length) return
        this.resetIfFinished()
        const allowed = new Set(this.typesValue.split(",").filter(Boolean))
        files.slice(0, this.constructor.MAX_FILES).forEach((file) => {
            const row = this.addRow(file)
            const problem = this.problemWith(file, allowed)
            if (problem) return this.markRow(row, "error", problem)
            this._queue.push({ file, row })
        })
        if (files.length > this.constructor.MAX_FILES) this.note(`Only the first ${this.constructor.MAX_FILES} files were taken. Add the rest in another batch.`)
        this.pump()
    }

    problemWith(file, allowed) {
        if (file.size === 0) return "Empty file"
        if (file.size > this.maxBytesValue) return `Over ${Math.round(this.maxBytesValue / 1048576)} MB`
        if (file.type && allowed.size && !allowed.has(file.type)) return "File type not supported"
        return null
    }

    pump() {
        while (this._active < this.constructor.CONCURRENCY && this._queue.length) {
            const job = this._queue.shift()
            this._active += 1
            this.send(job).finally(() => {
                this._active -= 1
                this.pump()
            })
        }
        if (this._active === 0 && !this._queue.length) this.settle()
    }

    send(job) {
        return new Promise((resolve) => {
            const xhr = new XMLHttpRequest()
            const data = new FormData()
            data.append("file", job.file)
            xhr.open("POST", this.urlValue)
            xhr.setRequestHeader("Accept", "application/json")
            xhr.setRequestHeader("X-CSRF-Token", csrfToken())
            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) this.progress(job.row, event.loaded / event.total)
            }
            xhr.onload = () => {
                let body = {}
                try { body = JSON.parse(xhr.responseText) } catch (_) { /* a non-JSON failure page */ }
                if (xhr.status >= 200 && xhr.status < 300 && body.ok) this.markRow(job.row, "done", "Added")
                else {
                    job.row.job = job
                    this.markRow(job.row, "error", body.error || (xhr.status === 413 ? "File too large" : "Upload failed"))
                }
                resolve()
            }
            xhr.onerror = () => {
                job.row.job = job
                this.markRow(job.row, "error", "Connection lost")
                resolve()
            }
            this.markRow(job.row, "uploading", "Uploading…")
            xhr.send(data)
        })
    }

    settle() {
        const done = this._rows.filter((row) => row.dataset.state === "done").length
        const failed = this._rows.filter((row) => row.dataset.state === "error").length
        if (!done && !failed) return
        this.summaryTarget.hidden = false
        this.summaryTarget.textContent = failed ? `${done} added, ${failed} could not be added.` : `${done} ${done === 1 ? "file" : "files"} added.`
        this.actionsTarget.hidden = false
        this.retryTarget.hidden = !this._rows.some((row) => row.dataset.state === "error" && row.job)
        this._finishedAt = Date.now()
        if (!failed && done) this._reloadTimer = window.setTimeout(() => this.finish(), 900)
    }

    retryFailed() {
        window.clearTimeout(this._reloadTimer)
        this._rows.filter((row) => row.dataset.state === "error" && row.job).forEach((row) => {
            this._queue.push(row.job)
            row.classList.remove("is-error")
        })
        this.summaryTarget.hidden = true
        this.actionsTarget.hidden = true
        this.pump()
    }

    finish() {
        window.clearTimeout(this._reloadTimer)
        const url = new URL(window.location.href)
        url.searchParams.delete("files")
        url.hash = "project-context"
        window.history.replaceState(null, "", url.toString())
        window.location.reload()
    }

    resetIfFinished() {
        window.clearTimeout(this._reloadTimer)
        if (this._active === 0 && !this._queue.length && this._finishedAt) {
            if (!this._rows.some((row) => row.dataset.state === "done")) {
                this.listTarget.replaceChildren()
                this._rows = []
            }
            this._finishedAt = null
        }
        this.summaryTarget.hidden = true
        this.actionsTarget.hidden = true
    }

    // --- Rows -------------------------------------------------------------------------

    addRow(file) {
        const li = document.createElement("li")
        li.className = "project-upload-row"
        li.dataset.state = "queued"
        const name = document.createElement("span")
        name.className = "project-upload-name"
        name.textContent = file.name
        name.title = file.name
        const size = document.createElement("small")
        size.textContent = this.humanSize(file.size)
        const status = document.createElement("span")
        status.className = "project-upload-status"
        status.textContent = "Waiting"
        const bar = document.createElement("span")
        bar.className = "project-upload-bar"
        bar.appendChild(document.createElement("i"))
        li.append(name, size, status, bar)
        this.listTarget.appendChild(li)
        this._rows.push(li)
        return li
    }

    markRow(row, state, message) {
        row.dataset.state = state
        row.classList.toggle("is-error", state === "error")
        row.querySelector(".project-upload-status").textContent = message
        if (state === "done") this.progress(row, 1)
    }

    progress(row, fraction) {
        row.querySelector(".project-upload-bar i").style.width = `${Math.round(fraction * 100)}%`
    }

    note(message) {
        this.summaryTarget.hidden = false
        this.summaryTarget.textContent = message
    }

    humanSize(bytes) {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`
        return `${(bytes / 1048576).toFixed(1)} MB`
    }
}
